import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { Button } from "#/components/ui/button";
import type { getMyEntitlements } from "#/server/getMyEntitlements";
import type { getMyPlayerProfile } from "#/server/getMyPlayerProfile";

type SubscriptionCardData = {
  profile: Awaited<ReturnType<typeof getMyPlayerProfile>>;
  entitlements: Awaited<ReturnType<typeof getMyEntitlements>>;
};

type PlayerSubscription = NonNullable<
  Awaited<ReturnType<typeof getMyPlayerProfile>>
>["subscription"];

function formatSubscriptionStatusText(subscription: PlayerSubscription | undefined): string {
  if (!subscription || subscription.status === "expired") {
    return "Subscription status: inactive";
  }
  const phase = subscription.phase ?? "paid";
  if (subscription.status === "active" && phase === "trial") {
    const trialEnd = subscription.trialEnd ?? subscription.currentPeriodEnd;
    return `Subscription status: trial (ends ${trialEnd})`;
  }
  if (subscription.status === "active") {
    return `Subscription status: active (ends ${subscription.currentPeriodEnd})`;
  }
  if (phase === "trial") {
    const trialEnd = subscription.trialEnd ?? subscription.currentPeriodEnd;
    return `Subscription status: canceled (trial ends ${trialEnd})`;
  }
  return `Subscription status: canceled (ends ${subscription.currentPeriodEnd})`;
}

export function hasSubscriptionCardFooter({ profile, entitlements }: SubscriptionCardData) {
  const isPremium = entitlements.tier === "premium";
  const hasBillingRecord = Boolean(profile.subscription);
  return !isPremium || hasBillingRecord;
}

export function SubscriptionCardStatus({ profile }: SubscriptionCardData) {
  return (
    <p className="text-sm text-muted-foreground">
      {formatSubscriptionStatusText(profile.subscription)}
    </p>
  );
}

export function SubscriptionCardFooter({ profile, entitlements }: SubscriptionCardData) {
  const isPremium = entitlements.tier === "premium";
  const hasBillingRecord = Boolean(profile.subscription);

  const actions: ReactNode[] = [];
  if (!isPremium) {
    actions.push(
      <Link key="upgrade" to="/premium" preload={false}>
        <Button variant="premium">Upgrade</Button>
      </Link>,
    );
  }
  if (hasBillingRecord) {
    actions.push(
      <a href="/api/portal" key="portal">
        <Button variant="outline">Manage billing</Button>
      </a>,
    );
  }

  if (actions.length === 0) return null;

  return <div className="flex flex-wrap gap-2">{actions}</div>;
}
